import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  ArrowLeft, MapPin, Eye, MessageSquare, Layers, Calendar, Ruler, 
  Package, ChevronLeft, ChevronRight, X, Send, Building2, Phone, Mail,
  CreditCard, Loader2, ShoppingCart, Download, Box, Sparkles, Leaf, ExternalLink,
  Award, ShieldCheck, History, FileText as FileTextIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import ModelViewer from '../components/ModelViewer';
import ContactSellerModal from '../components/ContactSellerModal';
import UpgradeModelModal from '../components/UpgradeModelModal';
import FavoriteButton from '../components/FavoriteButton';
import ReportListingButton from '../components/ReportListingButton';
import VerifiedSellerBadge from '../components/VerifiedSellerBadge';
import OpenInCAD from '../components/OpenInCAD';
import AddToProjectButton from '../components/AddToProjectButton';
import SimilarListings from '../components/SimilarListings';
import { formatListingLocation } from '../utils/location';
import { devLog, devError, devWarn } from '../utils/logger';

const conditionColors = {
  excellent: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
  good: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  fair: 'text-amber-400 bg-amber-400/10 border-amber-400/30',
  salvage: 'text-red-400 bg-red-400/10 border-red-400/30'
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [upgradingModel, setUpgradingModel] = useState(false);
  const [generating3DModel, setGenerating3DModel] = useState(false);
  const [show3DViewer, setShow3DViewer] = useState(false);
  const [imageFitMode, setImageFitMode] = useState('cover'); // 'cover' or 'contain'
  const [fullImageModal, setFullImageModal] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const res = await axios.get(`${API}/listings/${id}`);
        setListing(res.data);
        
        // Check if returning from successful model generation payment
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('model_generation') === 'success') {
          toast.success('Payment successful! AI model generation may take 1-2 minutes. Refresh to see the updated model.');
          // Clear the URL parameter
          window.history.replaceState({}, '', `/listing/${id}`);
        }
      } catch (error) {
        devError('Error fetching listing:', error);
        toast.error('Listing not found');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id, navigate]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleContactSeller = async () => {
    if (!user) {
      navigate('/auth', { state: { from: `/listing/${id}` } });
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API}/listings/${id}/inquiry`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Message sent to seller!');
      setContactModalOpen(false);
      setMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      navigate('/auth', { state: { from: `/listing/${id}` } });
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          listing_id: id,
          quantity: quantity,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      devError('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(false);
    }
  };

  const nextImage = () => {
    if (listing?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };


  const handleUpgradeToTier2 = async () => {
    if (!user || !token) {
      navigate('/auth', { state: { from: `/listing/${id}` } });
      return;
    }

    try {
      setUpgradingModel(true);
      
      // Call backend to initiate Stripe checkout for model generation
      const response = await axios.post(
        `${API}/models/generate`,
        {
          listing_id: listing.listing_id,
          tier: 'tier2',
          force: true
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.success) {
        toast.success('AI model generation completed!');
        setUpgradeModalOpen(false);
        
        // Refresh listing data
        const { data } = await axios.get(`${API}/listings/${id}`);
        setListing(data);
      }
    } catch (error) {
      devError('Upgrade error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upgrade model');
    } finally {
      setUpgradingModel(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!listing) return null;

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="listing-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to listings
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-card/50 mb-4 group">
              {listing.images?.[0] ? (
                <>
                  <img
                    src={listing.images[currentImageIndex]}
                    alt={listing.title}
                    className={`w-full h-full cursor-pointer transition-all ${
                      imageFitMode === 'cover' ? 'object-cover' : 'object-contain bg-background/50'
                    }`}
                    onClick={() => setFullImageModal(true)}
                    data-testid="main-image"
                  />
                  
                  {/* Fit Mode Toggle */}
                  <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setImageFitMode(imageFitMode === 'cover' ? 'contain' : 'cover')}
                      className="px-3 py-1.5 rounded-lg bg-black/70 text-white text-xs font-medium hover:bg-black/80 backdrop-blur-sm"
                      title={imageFitMode === 'cover' ? 'Fit to frame' : 'Fill frame'}
                    >
                      {imageFitMode === 'cover' ? '⊡ Fit' : '⊞ Fill'}
                    </button>
                  </div>
                  
                  {listing.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                        data-testid="prev-image-btn"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                        data-testid="next-image-btn"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                        <span className="px-2 py-1 rounded-md bg-black/70 text-white text-xs font-medium backdrop-blur-sm">
                          {currentImageIndex + 1} / {listing.images.length}
                        </span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Layers className="w-16 h-16 text-muted-foreground/30" />
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            {listing.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {listing.images.map((img, idx) => (
                  <button
                    key={`${listing.listing_id}-img-${idx}`}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-24 h-24 rounded-md overflow-hidden border-2 transition-all hover:scale-105 ${
                      idx === currentImageIndex ? 'border-accent ring-2 ring-accent/30' : 'border-border/40 hover:border-accent/50'
                    }`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {/* Badges */}
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 text-sm font-medium rounded-full badge-${listing.availability}`}>
                {listing.availability === 'available' ? 'Available' : listing.availability}
              </span>
              {listing.has_digital_model && (
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-primary/20 text-primary border border-primary/30">
                  3D Model Available
                </span>
              )}
            </div>

            {/* Title & Price */}
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="listing-title">
              {listing.title}
            </h1>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-playfair text-4xl font-bold text-gold-gradient" data-testid="listing-price">
                {formatPrice(listing.price)}
              </span>
              <span className="text-muted-foreground">per {listing.unit}</span>
            </div>

            {/* Save to project — appears next to price so it's the second
                most prominent buyer action after "Buy now" further down. */}
            <div className="mb-6">
              <AddToProjectButton listingId={listing.listing_id} />
            </div>

            {/* Condition & Category */}
            <div className="flex items-center gap-4 mb-6">
              <span className={`px-3 py-1 text-sm font-medium rounded-full border capitalize ${conditionColors[listing.condition]}`}>
                {listing.condition} condition
              </span>
              <span className="text-muted-foreground capitalize">{listing.category}</span>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 rounded-lg bg-card/50 border border-border/20">
                <Package className="w-5 h-5 text-accent mb-2" />
                <p className="text-sm text-muted-foreground">Quantity</p>
                <p className="font-semibold">{listing.quantity} {listing.unit}</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 border border-border/20">
                <MapPin className="w-5 h-5 text-accent mb-2" />
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-semibold line-clamp-1">{formatListingLocation(listing)}</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 border border-border/20">
                <Eye className="w-5 h-5 text-accent mb-2" />
                <p className="text-sm text-muted-foreground">Views</p>
                <p className="font-semibold">{listing.views}</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 border border-border/20">
                <MessageSquare className="w-5 h-5 text-accent mb-2" />
                <p className="text-sm text-muted-foreground">Inquiries</p>
                <p className="font-semibold">{listing.inquiries}</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h3 className="font-playfair text-lg font-semibold mb-3">Description</h3>
              <p className="text-muted-foreground leading-relaxed" data-testid="listing-description">
                {listing.description}
              </p>
            </div>

            {/* Specifications */}
            {(listing.material || listing.dimensions || listing.age || listing.sku) && (
              <div className="mb-8">
                <h3 className="font-playfair text-lg font-semibold mb-3">Specifications</h3>
                <div className="space-y-2">
                  {listing.sku && (
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground w-24">SKU:</span>
                      <span className="font-medium font-mono text-accent">{listing.sku}</span>
                    </div>
                  )}
                  {listing.material && (
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground w-24">Material:</span>
                      <span className="font-medium">{listing.material}</span>
                    </div>
                  )}
                  {listing.dimensions && (
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground w-24">Dimensions:</span>
                      <span className="font-medium">{listing.dimensions}</span>
                    </div>
                  )}
                  {listing.age && (
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground w-24">Age:</span>
                      <span className="font-medium">{listing.age}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Carbon savings — embodied-carbon estimate vs. buying new */}
            {listing.carbon?.kg_co2e_total >= 1 && (
              <div
                className="mb-8 p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5"
                data-testid="listing-carbon-panel"
              >
                <div className="flex items-start gap-3">
                  <Leaf className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-playfair text-lg font-semibold text-emerald-200 mb-1">
                      Avoids ~
                      {listing.carbon.kg_co2e_total >= 1000
                        ? `${(listing.carbon.kg_co2e_total / 1000).toFixed(2)} tonnes`
                        : `${Math.round(listing.carbon.kg_co2e_total)} kg`}{' '}
                      CO<sub>2</sub>e vs. buying new
                    </h3>
                    <p className="text-xs text-emerald-300/80 leading-relaxed">
                      Estimated embodied-carbon saving{' '}
                      {listing.quantity > 1 && (
                        <>
                          (≈ {Math.round(listing.carbon.kg_co2e_per_unit)} kg / unit ×{' '}
                          {listing.quantity} units){' '}
                        </>
                      )}
                      using{' '}
                      <span className="font-medium">
                        {listing.carbon.method === 'dims'
                          ? 'dimensional volume'
                          : 'category baseline'}
                      </span>{' '}
                      with the{' '}
                      <a
                        href="https://circularecology.com/embodied-carbon-footprint-database.html"
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-emerald-200"
                      >
                        Bath ICE v3.0
                      </a>{' '}
                      factor for{' '}
                      <span className="italic">{listing.carbon.factor_material || 'this material'}</span>{' '}
                      ({listing.carbon.factor_kg_co2e_per_kg} kg CO<sub>2</sub>e/kg).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Compliance block — material passport + Green Star Fitouts / Climate Active / NABERS badges */}
            <div
              className="mb-8 p-5 rounded-lg border border-border/30 bg-card/60"
              data-testid="listing-compliance-panel"
            >
              <div className="flex items-start gap-3 mb-3">
                <Award className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-playfair text-lg font-semibold">Compliance evidence</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Download a one-page Digital Material Passport to include in a Green Star Fitouts, Climate Active, or NABERS submission.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <a
                  href={`${API}/listings/${listing.listing_id}/material-passport`}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-accent text-accent-foreground hover:bg-accent/90 text-sm font-medium transition-colors"
                  data-testid="material-passport-btn"
                >
                  <FileTextIcon className="w-4 h-4" />
                  Download Material Passport
                </a>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                  <ShieldCheck className="w-3 h-3" /> Green Star Fitouts · Design for Reuse
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                  <ShieldCheck className="w-3 h-3" /> Climate Active · Upfront Carbon (A1–A5)
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                  <ShieldCheck className="w-3 h-3" /> NABERS · Embodied Emissions
                </span>
              </div>

              {/* Salvage origin — visible chain of custody */}
              {(listing.salvage_origin && (
                listing.salvage_origin.building_name ||
                listing.salvage_origin.city ||
                listing.salvage_origin.year_built ||
                listing.salvage_origin.year_deconstructed ||
                listing.salvage_origin.notes ||
                listing.salvage_origin.project_reference
              )) ? (
                <div
                  className="mt-4 pt-4 border-t border-border/30 text-sm"
                  data-testid="salvage-origin-block"
                >
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-accent mb-2">
                    <History className="w-3 h-3" /> Salvage origin &amp; chain of custody
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                    {listing.salvage_origin.building_name && (
                      <div><span className="text-muted-foreground">Originally installed at:</span> <span className="font-medium">{listing.salvage_origin.building_name}</span></div>
                    )}
                    {listing.salvage_origin.city && (
                      <div><span className="text-muted-foreground">Location:</span> <span className="font-medium">{listing.salvage_origin.city}</span></div>
                    )}
                    {listing.salvage_origin.year_built && (
                      <div><span className="text-muted-foreground">Year built:</span> <span className="font-medium">{listing.salvage_origin.year_built}</span></div>
                    )}
                    {listing.salvage_origin.year_deconstructed && (
                      <div><span className="text-muted-foreground">Year deconstructed:</span> <span className="font-medium">{listing.salvage_origin.year_deconstructed}</span></div>
                    )}
                    {listing.salvage_origin.project_reference && (
                      <div className="md:col-span-2"><span className="text-muted-foreground">Reference:</span> <span className="font-medium">{listing.salvage_origin.project_reference}</span></div>
                    )}
                    {listing.salvage_origin.notes && (
                      <div className="md:col-span-2 text-muted-foreground italic mt-1">"{listing.salvage_origin.notes}"</div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {/* 3D Models Section */}
            <div className="mb-8">
              <OpenInCAD listingId={listing.listing_id} />
            </div>

            <div className="mb-8">
              <h3 className="font-playfair text-lg font-semibold mb-3 flex items-center gap-2">
                <Box className="w-5 h-5 text-accent" />
                3D Models (BIM/Revit)
              </h3>
              
              {listing.has_digital_model && listing.model_ifc_url ? (
                <div className="space-y-4">
                  {/* Interactive 3D Preview */}
                  {show3DViewer && listing.model_glb_url && (
                    <div className="mb-4">
                      <ModelViewer 
                        modelUrl={`${BACKEND_URL}${listing.model_glb_url}`}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        Preview of basic geometric model • Download for full BIM data
                      </p>
                    </div>
                  )}
                  
                  {/* Download Buttons - Primary Feature */}
                  
                  {/* Download Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`${BACKEND_URL}${listing.model_ifc_url}`}
                      download
                      className="flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
                    >
                      <Download className="w-5 h-5" />
                      Download IFC Model (for Revit)
                    </a>
                    
                    {listing.model_glb_url && (
                      <>
                        <a
                          href={`${BACKEND_URL}${listing.model_glb_url}`}
                          download
                          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-secondary text-foreground hover:bg-secondary/80 transition-colors font-medium"
                        >
                          <Download className="w-5 h-5" />
                          Download GLB Model
                        </a>
                        
                        <button
                          onClick={() => setShow3DViewer(!show3DViewer)}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border/40 hover:bg-accent/10 transition-colors"
                        >
                          <Box className="w-4 h-4" />
                          {show3DViewer ? 'Hide' : 'Preview'} 3D Model
                        </button>
                        
                        {/* Upgrade to Tier 2 AI Model */}
                        {listing.model_tier === 'tier1' && (
                          <button
                            onClick={() => setUpgradeModalOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 transition-all font-medium"
                          >
                            <Sparkles className="w-4 h-4" />
                            Upgrade to AI Model
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Model Info */}
                  <div className="p-3 rounded-lg bg-card/30 border border-border/20 text-sm text-muted-foreground">
                    <p>
                      <span className="font-medium text-foreground">
                        {listing.model_tier === 'tier1' ? 'Standard Model' : 'AI-Generated Model'}
                      </span>
                      {' • '}
                      Generated {listing.model_generated_at ? new Date(listing.model_generated_at).toLocaleDateString() : ''}
                      {listing.model_tier === 'tier1' && ' • Based on dimensions'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-6 rounded-lg bg-card/30 border border-border/20 text-center">
                  <Box className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">
                    No 3D model available for this listing
                  </p>
                  
                  {/* Generate Model Button (for designers) */}
                  {user && user.user_id !== listing.supplier_id && (
                    <Button
                      onClick={async () => {
                        if (!token) {
                          toast.error('Please sign in to generate 3D models');
                          return;
                        }
                        
                        if (window.confirm('Generate a 3D model for this listing? Cost: $1.00 AUD\n\nOnce generated, this model will be free for all future users.')) {
                          setGenerating3DModel(true);
                          try {
                            // Initiate payment and generation
                            const response = await axios.post(
                              `${API}/models/generate`,
                              { listing_id: listing.listing_id, tier: 'tier2' },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            
                            // In production, integrate Stripe payment here
                            toast.success('3D model generation initiated! This may take up to 60 seconds...');
                            
                            // For now, just show success (in production, handle payment flow)
                            setTimeout(() => {
                              window.location.reload();
                            }, 2000);
                          } catch (error) {
                            devError('Generation error:', error);
                            toast.error(error.response?.data?.detail || 'Failed to generate model');
                          } finally {
                            setGenerating3DModel(false);
                          }
                        }
                      }}
                      disabled={generating3DModel}
                      className="btn-gold"
                    >
                      {generating3DModel ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Box className="w-4 h-4 mr-2" />
                          Generate 3D Model ($1.00)
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>


            {/* Supplier Info */}
            <div className="p-6 rounded-lg bg-card/50 border border-border/20 mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(`/marketplace?supplier=${encodeURIComponent(listing.supplier_company || listing.supplier_name)}`)}
                      className="font-semibold hover:text-accent hover:underline transition-colors text-left"
                    >
                      {listing.supplier_company || listing.supplier_name}
                    </button>
                    {listing.is_verified_seller && <VerifiedSellerBadge size="lg" />}
                  </div>
                  <p className="text-sm text-muted-foreground">Click to view all listings</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {/* Buy Now - Only show if listing is available and user is not the supplier */}
              {listing.availability === 'available' && user?.user_id !== listing.supplier_id && (
                <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-muted-foreground">Quantity</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-8 h-8 rounded bg-secondary/50 text-foreground hover:bg-secondary"
                        disabled={quantity <= 1}
                      >
                        -
                      </button>
                      <span className="w-12 text-center font-medium">{quantity}</span>
                      <button
                        onClick={() => setQuantity(Math.min(listing.quantity, quantity + 1))}
                        className="w-8 h-8 rounded bg-secondary/50 text-foreground hover:bg-secondary"
                        disabled={quantity >= listing.quantity}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-playfair text-2xl font-bold text-gold-gradient">
                      {formatPrice(listing.price * quantity)}
                    </span>
                  </div>
                  <Button 
                    onClick={handleBuyNow}
                    disabled={purchasing}
                    className="w-full btn-gold py-6 text-lg"
                    data-testid="buy-now-btn"
                  >
                    {purchasing ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-5 h-5 mr-2" />
                    )}
                    {purchasing ? 'Processing...' : 'Buy Now'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Secure payment via Stripe
                  </p>
                </div>
              )}

              <div className="flex gap-4">
                {listing.external_source && listing.external_url ? (
                  // External listing — we don't transact this stock ourselves;
                  // bounce the buyer to the original Gumtree/FB/Craigslist page.
                  <a
                    href={listing.external_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-md btn-gold py-6 font-medium"
                    data-testid="external-view-btn"
                  >
                    <ExternalLink className="w-5 h-5" />
                    View on {listing.external_source === 'gumtree' ? 'Gumtree'
                      : listing.external_source === 'fb_marketplace' ? 'Facebook Marketplace'
                      : listing.external_source === 'craigslist' ? 'Craigslist'
                      : 'source'}
                  </a>
                ) : (
                  <Button 
                    onClick={() => setContactModalOpen(true)}
                    variant={listing.availability === 'available' && user?.user_id !== listing.supplier_id ? "outline" : "default"}
                    className={listing.availability === 'available' && user?.user_id !== listing.supplier_id 
                      ? "flex-1 border-accent/40 text-accent hover:bg-accent/10 py-6" 
                      : "flex-1 btn-gold py-6"
                    }
                    data-testid="contact-seller-btn"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Contact Seller
                  </Button>
                )}
                {listing.has_digital_model && listing.digital_model_url && (
                  <Button 
                    variant="outline"
                    className="border-primary/40 text-primary hover:bg-primary/10 py-6"
                    data-testid="download-model-btn"
                  >
                    <Layers className="w-5 h-5 mr-2" />
                    3D Model
                  </Button>
                )}
                <FavoriteButton listingId={listing.listing_id} className="py-6" />
              </div>

              {/* Admin-only edit link — lets the platform owner fix photos /
                  typos / wrong categories on any listing without logging in
                  as the supplier. Only shown when the viewer is an admin. */}
              {user && (user.user_type === 'admin' || (user.email && ['thisisradas@gmail.com'].includes(user.email.toLowerCase()))) && user.user_id !== listing.supplier_id && (
                <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
                  <button
                    type="button"
                    onClick={() => navigate(`/edit-listing/${listing.listing_id}`)}
                    className="text-muted-foreground hover:text-accent inline-flex items-center gap-1.5"
                    data-testid="admin-edit-listing-btn"
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
                    Admin — edit this listing
                  </button>
                  <span className="text-muted-foreground/30">•</span>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/listings/${listing.listing_id}/feature`);
                        setListing((p) => p ? { ...p, is_featured: data.is_featured } : p);
                        toast.success(data.is_featured ? 'Featured on landing page' : 'Removed from featured');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Action failed');
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 ${listing.is_featured ? 'text-accent' : 'text-muted-foreground hover:text-accent'}`}
                    data-testid="admin-toggle-feature-btn"
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${listing.is_featured ? 'bg-accent' : 'bg-amber-400'}`} />
                    {listing.is_featured ? 'Featured ✓ (click to unfeature)' : 'Mark as featured'}
                  </button>
                </div>
              )}

              {/* Report listing — anyone can flag (anon OK, IP-rate-limited).
                  Sits subtly under the action buttons. */}
              <div className="mt-4 pt-4 border-t border-border/10">
                <ReportListingButton listingId={listing.listing_id} user={user} />
              </div>
            </div>
          </div>
        </div>

        {/* Similar listings — slim carousel below the main two-column area. */}
        <SimilarListings listingId={listing.listing_id} />
      </div>

      {/* Contact Modal */}
      <ContactSellerModal
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
        listing={listing}
        message={message}
        setMessage={setMessage}
        sending={sending}
        onSend={handleContactSeller}
        formatPrice={formatPrice}
      />

      {/* Upgrade to Tier 2 Modal */}
      <UpgradeModelModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        user={user}
        listing={listing}
        upgradingModel={upgradingModel}
        onUpgrade={handleUpgradeToTier2}
      />

      {/* Full Image Modal */}
      <Dialog open={fullImageModal} onOpenChange={setFullImageModal}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden">
          <div className="relative bg-background">
            <button
              onClick={() => setFullImageModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-black/80 backdrop-blur-sm"
            >
              <X className="w-5 h-5" />
            </button>
            
            <img
              src={listing?.images?.[currentImageIndex]}
              alt={listing?.title}
              className="w-full max-h-[90vh] object-contain bg-background"
            />
            
            {listing?.images?.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-black/80 backdrop-blur-sm"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-black/80 backdrop-blur-sm"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg bg-black/70 text-white text-sm font-medium backdrop-blur-sm">
                  {currentImageIndex + 1} / {listing.images.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
